import React, { useState, useEffect, useRef } from 'react';
import {
  Loader2,
  ShieldCheck,
  AlertCircle,
  History,
  Plus,
  Check,
  Sparkles,
  Search,
  Ban,
  Target,
} from '../../../icons';
import {
  aiAnalyzeChannel,
  aiService,
  type ChannelAnalysisRequest,
  type NewsAnalysisRequest,
  type TaskResponse,
} from '../api';
import { useAiReviewSSE } from '@discovr/common';
import ProcessingPanel from '../ProcessingPanel';
import NewsAnalysisSection from '../news-analysis';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { YoutubeIcon } from '../../icons';
import type { AnalysisResult, VerdictV2, BrandSafetyWorkspaceProps } from './types';
import type { NewsAnalysisResponse } from '../news-analysis';
import {
  PLATFORMS,
  CHANNEL_WIZARD_STEPS,
  NEWS_WIZARD_STEPS,
  BRAND_SAFETY_NEWS_CACHE_PREFIX,
  BRAND_SAFETY_CACHE_TTL_MS,
  DEFAULT_BANNED_KEYWORDS,
  DEFAULT_SENSITIVE_TOPICS,
} from './constants';
import { RiskSignalsGrid, ChannelDashboard, BrandSafetySkeleton } from './ChannelDashboard';
import { InstagramBrandSafetyDashboard } from './instagram/InstagramBrandSafety';
import { CreatorProfileSummary } from './youtube/CreatorProfileSummary';
import { NewsResultsDashboard } from './NewsResultsDashboard';

// Static keyframes injected once via useEffect (no user data, safe)
const STATIC_KEYFRAMES = [
  '@keyframes spin { 100% { transform: rotate(360deg); } }',
  '@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }',
].join('\n');

function useStaticStyles(css: string) {
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = css;
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [css]);
}

// The safety endpoint returns a dynamic envelope (`{ status, username, data,
// error }`); narrow to the fields the workspace reads instead of `any`.
interface ChannelSafetyEnvelope {
  status?: string;
  data?: unknown;
  error?: string;
  username?: string;
}

// Superset of the fields the creator-profile blob is read for here and the
// (private, non-exported) shapes CreatorProfileSummary + InstagramBrandSafety
// consume — so this stays assignable to both without an `any`.
interface CreatorProfileBlob {
  platform?: string;
  content_safety_tier?: string;
  primary_topics?: string[];
  content_themes?: string[];
  audience_type?: string;
  audience_sentiment?: string;
  brand_collab_signals?: string;
  overall_summary?: string;
  brand_fit_summary?: string;
  fit_summary?: string;
  profile_meta?: { posts_count?: number };
  summary?: Record<string, number | undefined>;
  posts?: unknown[];
  risk_signals?: unknown;
}

// The SUCCESS `taskResult.result` blob. YouTube nests analysis under
// `channel_analysis`; Instagram sets its status to "not_applicable". The index
// signature covers the legacy shape where the blob itself is the AnalysisResult.
interface BrandSafetyResultBlob {
  channel_analysis?: (AnalysisResult & { status?: string }) | null;
  creator_profile?: CreatorProfileBlob | null;
  news_analysis?: NewsAnalysisResponse | null;
  verdict_v2?: VerdictV2 | null;
  [key: string]: unknown;
}

export default function BrandSafetyWorkspace({
  initialChannelUrl,
  autoRun = false,
  hideSidebar = false,
  rosterEntryId,
  historyPanel,
  onSaveSession,
  userId,
  role = 'brand',
  onApprove,
}: BrandSafetyWorkspaceProps) {
  useStaticStyles(STATIC_KEYFRAMES);

  const normalizeChannelUrl = (raw: string): string => {
    const v = String(raw || '').trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    if (v.includes('youtube.com') || v.includes('youtu.be')) return `https://${v}`;
    if (v.startsWith('@')) return `https://www.youtube.com/${v}`;
    return `https://www.youtube.com/@${v}`;
  };
  const readChannelCache = (url: string): TaskResponse | null => {
    void url;
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('discovr:brand-safety:')) localStorage.removeItem(k);
      });
    } catch {
      // ignore — quota / disabled storage
    }
    return null;
  };
  const writeChannelCache = (url: string, result: TaskResponse) => {
    void url;
    void result;
  };
  const normalizeNewsKey = (name: string) => String(name || '').trim().toLowerCase();
  const newsKeyPrefix = userId
    ? `${BRAND_SAFETY_NEWS_CACHE_PREFIX}${userId}:`
    : BRAND_SAFETY_NEWS_CACHE_PREFIX;
  const readNewsCache = (name: string): TaskResponse | null => {
    const key = normalizeNewsKey(name);
    if (!key) return null;
    try {
      const raw = localStorage.getItem(`${newsKeyPrefix}${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts?: number; taskResult?: TaskResponse };
      if (!parsed?.ts || Date.now() - parsed.ts > BRAND_SAFETY_CACHE_TTL_MS) return null;
      return parsed.taskResult ?? null;
    } catch {
      return null;
    }
  };
  const writeNewsCache = (name: string, result: TaskResponse) => {
    const key = normalizeNewsKey(name);
    if (!key) return;
    try {
      localStorage.setItem(
        `${newsKeyPrefix}${key}`,
        JSON.stringify({ ts: Date.now(), taskResult: result }),
      );
    } catch {
      // ignore storage failures
    }
  };
  // Channel Analysis state
  const [formData, setFormData] = useState<ChannelAnalysisRequest>({
    channel_url: initialChannelUrl || '',
    banned_keywords: [],
    sensitive_topics: [],
    target_platforms: ['YouTube'],
  });
  const [bannedKeywords, setBannedKeywords] = useState<string[]>([]);
  const [sensitiveTopics, setSensitiveTopics] = useState<string[]>([]);
  const [bannedNewInput, setBannedNewInput] = useState('');
  const [sensitiveNewInput, setSensitiveNewInput] = useState('');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  // True only during the initial cache fetch (autoRun embedded mode). Shows a
  // lightweight spinner instead of the full ProcessingPanel so re-opening a
  // cached analysis doesn't flash "Analyzing… Ns".
  const [initialFetching, setInitialFetching] = useState(!!(autoRun && rosterEntryId));
  // Parked: the "fake polling" skeleton is never toggled on yet — keep the read
  // flag (drives the skeleton branch) but drop the unused setter.
  const [fakePolling] = useState(false);
  const [taskResult, setTaskResult] = useState<TaskResponse | null>(null);
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);
  const [_isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const pollRef = useRef<number | null>(null);
  // Channel elapsed timer: only the setter is read (drives the interval); the
  // channel flow renders a skeleton, not the elapsed count (news uses its own).
  const [_elapsedSecs, setElapsedSecs] = useState(0);
  const elapsedRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  // News Analysis state
  const [activeMode, setActiveMode] = useState<'channel' | 'news'>('channel');
  const [channelWizardStep, setChannelWizardStep] = useState(1);
  const [newsForm, setNewsForm] = useState<NewsAnalysisRequest>({
    creator_name: '',
    channel_name: '',
  });
  const [newsTaskId, setNewsTaskId] = useState<string | null>(null);
  const [newsPolling, setNewsPolling] = useState(false);
  const [newsResult, setNewsResult] = useState<TaskResponse | null>(null);
  const [newsElapsedSecs, setNewsElapsedSecs] = useState(0);
  const newsPollRef = useRef<number | null>(null);
  const newsElapsedRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset the wizard to step 1 whenever the mode switches. Adjust-during-render
  // with prev-tracking (guarded by the equality check, so it can't loop) —
  // avoids a setState-in-effect and the extra commit it would cost.
  const [prevActiveMode, setPrevActiveMode] = useState(activeMode);
  if (prevActiveMode !== activeMode) {
    setPrevActiveMode(activeMode);
    setChannelWizardStep(1);
  }

  const handleNew = () => {
    setSessionId(crypto.randomUUID());
    setFormData({
      channel_url: '',
      banned_keywords: [],
      sensitive_topics: [],
      target_platforms: ['YouTube'],
    });
    setBannedKeywords([]);
    setSensitiveTopics([]);
    setBannedNewInput('');
    setSensitiveNewInput('');
    setTaskId(null);
    setTaskResult(null);
    setPolling(false);
    setNewsTaskId(null);
    setNewsResult(null);
    setNewsPolling(false);
    setNewsElapsedSecs(0);
    setChannelWizardStep(1);
    if (newsElapsedRef.current) clearInterval(newsElapsedRef.current);
    if (newsPollRef.current) clearInterval(newsPollRef.current);
  };

  const togglePlatform = (p: string) =>
    setFormData((prev) => ({
      ...prev,
      target_platforms: prev.target_platforms.includes(p)
        ? prev.target_platforms.filter((x) => x !== p)
        : [...prev.target_platforms, p],
    }));

  const buildPayload = (): ChannelAnalysisRequest => ({
    channel_url: normalizeChannelUrl(formData.channel_url),
    banned_keywords: Array.from(new Set([...DEFAULT_BANNED_KEYWORDS, ...bannedKeywords])),
    sensitive_topics: Array.from(new Set([...DEFAULT_SENSITIVE_TOPICS, ...sensitiveTopics])),
    target_platforms: formData.target_platforms,
  });

  const extractCreatorName = (url: string): string => {
    try {
      const parts = new URL(url).pathname.split('/').filter(Boolean);
      const last = parts[parts.length - 1] || '';
      return last.replace(/^@/, '');
    } catch {
      return '';
    }
  };

  const startNewsAnalysis = async (creatorName: string) => {
    const normalizedName = creatorName.trim();
    if (!normalizedName) return;
    setNewsForm((p) => ({ ...p, creator_name: normalizedName, channel_name: normalizedName }));

    const cached = readNewsCache(normalizedName);
    if (cached && cached.status === 'SUCCESS' && cached.result) {
      setNewsTaskId(cached.task_id || null);
      setNewsResult(cached);
      setNewsPolling(false);
      return;
    }

    setNewsResult(null);
    setNewsTaskId(null);
    setNewsPolling(false);
  };

  const triggerAnalysis = async (url: string = formData.channel_url) => {
    const normalizedUrl = normalizeChannelUrl(url);
    if (!normalizedUrl || !/^https?:\/\//i.test(normalizedUrl)) {
      setTaskResult({
        task_id: '',
        status: 'FAILURE',
        error: 'Please enter a valid YouTube channel URL or handle.',
      });
      setPolling(false);
      return;
    }

    if (normalizedUrl.includes('instagram.com')) {
      setTaskResult({
        task_id: '',
        status: 'FAILURE',
        error: 'Instagram brand-safety analysis is not yet available.',
      });
      setPolling(false);
      return;
    }

    const cached = readChannelCache(normalizedUrl);
    if (cached && cached.status === 'SUCCESS' && cached.result) {
      setTaskId(cached.task_id || null);
      setTaskResult(cached);
      setPolling(false);
      return;
    }

    const creatorName = extractCreatorName(normalizedUrl);
    if (creatorName) {
      startNewsAnalysis(creatorName);
    }

    const payload = {
      channel_url: normalizedUrl,
      banned_keywords: Array.from(new Set([...DEFAULT_BANNED_KEYWORDS, ...bannedKeywords])),
      sensitive_topics: Array.from(new Set([...DEFAULT_SENSITIVE_TOPICS, ...sensitiveTopics])),
      target_platforms: formData.target_platforms,
    };
    setTaskId(null);
    setTaskResult(null);
    setPolling(true);
    onSaveSession?.('brandsafety', sessionId, {
      title: `Brand Safety: ${payload.channel_url}`,
      messages: [
        {
          role: 'user',
          content: `Analyzing: ${payload.channel_url}`,
          metadata: { request: payload },
          timestamp: new Date().toISOString(),
        },
      ],
    });
    try {
      if (!rosterEntryId) {
        throw new Error('rosterEntryId is required for brand safety analysis');
      }
      const envelope = (await aiAnalyzeChannel(undefined, rosterEntryId, role)) as ChannelSafetyEnvelope;

      if (envelope?.status === 'complete' && envelope.data) {
        const taskRes: TaskResponse = {
          task_id: rosterEntryId,
          status: 'SUCCESS',
          result: envelope.data,
        };
        setTaskResult(taskRes);
        setPolling(false);
        setPendingUsername(null);
        writeChannelCache(payload.channel_url, taskRes);
        onSaveSession?.('brandsafety', sessionId, {
          title: `Brand Safety: ${payload.channel_url}`,
          messages: [
            {
              role: 'user',
              content: `Analyzing: ${payload.channel_url}`,
              metadata: { request: payload },
              timestamp: new Date().toISOString(),
            },
            {
              role: 'assistant',
              content: 'Analysis complete',
              metadata: { result: envelope.data },
              timestamp: new Date().toISOString(),
            },
          ],
        });
      } else if (envelope?.status === 'failed') {
        setTaskResult({
          task_id: rosterEntryId,
          status: 'FAILURE',
          error: envelope.error ?? 'Analysis failed',
        });
        setPolling(false);
        setPendingUsername(null);
      } else {
        setTaskId(rosterEntryId);
        setTaskResult(null);
        setPolling(true);
        setPendingUsername(typeof envelope?.username === 'string' ? envelope.username : null);
      }
    } catch (err) {
      setTaskResult({ task_id: '', status: 'FAILURE', error: String(err) });
      setPolling(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    triggerAnalysis(formData.channel_url);
  };

  useEffect(() => {
    if (!autoRun || taskId || polling || taskResult) return;
    if (initialChannelUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time autoRun kickoff driven by props; triggerAnalysis sequences async fetch + ordered setState, not a value derivable during render
      triggerAnalysis(initialChannelUrl);
      return;
    }
    if (!rosterEntryId) return;
    (async () => {
      // Don't setPolling here — we show initialFetching spinner instead.
      // setPolling(true) only once we know the job is actually in-flight.
      try {
        const envelope = (await aiAnalyzeChannel(undefined, rosterEntryId, role)) as ChannelSafetyEnvelope;
        if (!mountedRef.current) return;
        if (envelope?.status === 'complete' && envelope.data) {
          setTaskResult({ task_id: rosterEntryId, status: 'SUCCESS', result: envelope.data });
          setPolling(false);
          setPendingUsername(null);
        } else if (envelope?.status === 'failed') {
          setTaskResult({
            task_id: rosterEntryId,
            status: 'FAILURE',
            error: envelope.error ?? 'Analysis failed',
          });
          setPolling(false);
        } else {
          // Job is actually running — switch to full ProcessingPanel.
          setPolling(true);
          setTaskId(rosterEntryId);
          setPendingUsername(typeof envelope?.username === 'string' ? envelope.username : null);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setTaskResult({ task_id: '', status: 'FAILURE', error: String(err) });
        setPolling(false);
      } finally {
        if (mountedRef.current) setInitialFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, initialChannelUrl, rosterEntryId, role]);

  useEffect(() => {
    if (!polling || !taskId) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const envelope = (await aiAnalyzeChannel(undefined, taskId, role)) as ChannelSafetyEnvelope;
        if (!mountedRef.current) return;
        if (envelope?.status === 'complete' && envelope.data) {
          const taskRes: TaskResponse = {
            task_id: taskId,
            status: 'SUCCESS',
            result: envelope.data,
          };
          setTaskResult(taskRes);
          setPolling(false);
          setPendingUsername(null);
          if (pollRef.current) clearInterval(pollRef.current);
          const p = buildPayload();
          writeChannelCache(p.channel_url, taskRes);
          onSaveSession?.('brandsafety', sessionId, {
            title: `Brand Safety: ${p.channel_url}`,
            messages: [
              {
                role: 'user',
                content: `Analyzing: ${p.channel_url}`,
                metadata: { request: p },
                timestamp: new Date().toISOString(),
              },
              {
                role: 'assistant',
                content: 'Analysis complete.',
                metadata: { result: envelope.data },
                timestamp: new Date().toISOString(),
              },
            ],
          });
        } else if (envelope?.status === 'failed') {
          setTaskResult({
            task_id: taskId,
            status: 'FAILURE',
            error: envelope.error ?? 'Analysis failed',
          });
          setPolling(false);
          setPendingUsername(null);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        /* ignore polling errors */
      }
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [polling, taskId]);

  useEffect(() => {
    if (polling || fakePolling) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- this effect owns an interval side effect (elapsed ticker); the reset seeds the counter before the timer starts and is gated by the polling flags, so it can't loop
      if (!polling) setElapsedSecs(0);
      elapsedRef.current = window.setInterval(() => setElapsedSecs((s) => s + 1), 150);
    } else {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    }
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [polling, fakePolling]);

  useAiReviewSSE<{ username?: string; status?: string }>({
    eventName: 'ai_brand_safety_review_ready',
    enabled: polling && !!pendingUsername && !!taskId,
    filter: (p) => typeof p.username === 'string' && p.username === pendingUsername,
    onReady: async () => {
      if (!taskId) return;
      try {
        const envelope = (await aiAnalyzeChannel(undefined, taskId, role)) as ChannelSafetyEnvelope;
        if (envelope?.status === 'complete' && envelope.data) {
          const taskRes: TaskResponse = {
            task_id: taskId,
            status: 'SUCCESS',
            result: envelope.data,
          };
          setTaskResult(taskRes);
          setPolling(false);
          setPendingUsername(null);
          if (pollRef.current) clearInterval(pollRef.current);
          const p = buildPayload();
          writeChannelCache(p.channel_url, taskRes);
        } else if (envelope?.status === 'failed') {
          setTaskResult({
            task_id: taskId,
            status: 'FAILURE',
            error: envelope.error ?? 'Analysis failed',
          });
          setPolling(false);
          setPendingUsername(null);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        /* ignore — polling effect will retry on its own tick */
      }
    },
  });

  const handleNewsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsResult(null);
    setNewsTaskId(null);
    setNewsPolling(false);
  };

  useEffect(() => {
    if (!newsPolling || !newsTaskId) return;
    newsPollRef.current = window.setInterval(async () => {
      try {
        const res = await aiService.checkTaskStatus(newsTaskId);
        setNewsResult(res);
        if (res.status === 'SUCCESS' || res.status === 'FAILURE') {
          setNewsPolling(false);
          if (newsPollRef.current) clearInterval(newsPollRef.current);
          if (newsElapsedRef.current) clearInterval(newsElapsedRef.current);
          if (res.status === 'SUCCESS' && res.result) {
            writeNewsCache(newsForm.creator_name || newsForm.channel_name || '', res);
          }
        }
      } catch {
        /* ignore */
      }
    }, 1000);
    return () => {
      if (newsPollRef.current) clearInterval(newsPollRef.current);
    };
  }, [newsPolling, newsTaskId]);

  const rawResult: BrandSafetyResultBlob | null =
    taskResult?.status === 'SUCCESS' ? (taskResult.result as BrandSafetyResultBlob) : null;
  // Instagram brand safety: { creator_profile: {...}, channel_analysis: { status: "not_applicable", ... } }
  // YouTube brand safety:   { creator_profile: {...}, channel_analysis: { summary, channel, videos, ... } }
  // The AI pipeline always includes channel_analysis but sets status:"not_applicable" for Instagram.
  const channelAnalysis = rawResult?.channel_analysis;
  const isInstagramResult = !!(
    rawResult?.creator_profile &&
    (!channelAnalysis || channelAnalysis?.status === 'not_applicable')
  );
  const analysisData: AnalysisResult | null = rawResult && !isInstagramResult
    // Legacy shape: when there's no channel_analysis the blob itself is the
    // AnalysisResult (guarded above by !isInstagramResult).
    ? ((channelAnalysis ?? rawResult) as AnalysisResult)
    : null;
  const creatorProfile: CreatorProfileBlob | null = rawResult?.creator_profile ?? null;
  const newsAnalysis: NewsAnalysisResponse | null = rawResult?.news_analysis ?? null;
  const verdictV2 = rawResult?.verdict_v2 ?? null;

  // In embedded autoRun mode the report area is always "open" once we have a
  // rosterEntryId so the outer "Preparing analysis…" fallback never shows.
  // That fallback is for the manual (non-embedded) flow where the user hasn't
  // submitted a URL yet.
  const channelReportOpen =
    activeMode === 'channel' && (
      initialFetching || polling || fakePolling || taskId !== null || taskResult !== null ||
      (autoRun && !!rosterEntryId)
    );
  const newsReportOpen =
    activeMode === 'news' && (newsPolling || newsTaskId !== null || newsResult !== null);
  const showSetupWizard = !hideSidebar && !channelReportOpen && !newsReportOpen;
  const wizardSteps = activeMode === 'channel' ? CHANNEL_WIZARD_STEPS : NEWS_WIZARD_STEPS;
  const currentWizardStep = activeMode === 'channel' ? channelWizardStep : 1;
  const stepMeta = wizardSteps.find((s) => s.num === currentWizardStep) ?? wizardSteps[0];
  const goChannelStep = (n: number) =>
    setChannelWizardStep(Math.min(Math.max(1, n), CHANNEL_WIZARD_STEPS.length));

  const labelCss: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    fontWeight: 700,
    color: '#737373',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };
  const sectionCss: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #E5E5E5',
    borderRadius: 8,
    padding: '16px',

  };

  const reportShellClass = hideSidebar ? 'bsa-embed-report-shell' : 'sw-fullscreen';

  return (
    <>
      {historyPanel}
      <div className={hideSidebar ? 'agent-tab-host agent-tab-host--embed' : 'agent-tab-host'}>
        {activeMode === 'channel' && channelReportOpen && (
          <div className={reportShellClass} style={{ background: '#F5F5F5' }}>
            {(initialFetching || (autoRun && !!rosterEntryId && !polling && !fakePolling && !taskId && !taskResult)) ? (
              <BrandSafetySkeleton />
            ) : (polling || fakePolling) ? (
              <BrandSafetySkeleton />
            ) : !taskId && !taskResult ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#ffffff',
                  border: '1px solid #E5E5E5',
                  borderRadius: 16,
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: '#f5f5f5',
                    border: '1px solid #E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                  }}
                >
                  <ShieldCheck size={32} color="#737373" />
                </div>
                <h3 className="m-0 text-heading-h5 text-foreground">
                  Brand Safety Dashboard
                </h3>
                <p
                  style={{
                    color: '#737373',
                    marginTop: 10,
                    textAlign: 'center',
                    maxWidth: 380,
                    fontSize: 15,
                    lineHeight: 1.7,
                  }}
                >
                  Enter a YouTube channel URL to scan all videos from the last 12 months and get
                  full sentiment, transcript, and risk analysis on every video.
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 20,
                    justifyContent: 'center',
                  }}
                >
                  {[
                    'Last 12 months',
                    'All videos',
                    '500 comments/video',
                    'Transcript AI',
                    'Pre-aggregated sentiment',
                    'Safety radar',
                  ].map((f) => (
                    <span
                      key={f}
                      style={{
                        fontSize: 15,
                        padding: '4px 12px',
                        borderRadius: 99,
                        background: '#f5f5f5',
                        color: '#171717',
                        border: '1px solid #e0dedb',
                        fontWeight: 600,
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {/* No "Safety Analysis Report" header bar — not in Figma; the
                    analysisData.error path below handles the failure state. */}
                {/* Instagram: gated on creatorProfile (analysisData is always null for IG) */}
                {isInstagramResult && creatorProfile && (
                  <div className="flex flex-col gap-6">
                    <InstagramBrandSafetyDashboard data={creatorProfile} verdictV2={verdictV2} />
                    <NewsAnalysisSection news={newsAnalysis} isInstagram />
                  </div>
                )}
                {/* YouTube: gated on analysisData */}
                {!isInstagramResult && analysisData && !analysisData.error && (
                  <>
                    <CreatorProfileSummary
                      onApprove={onApprove}
                      profile={creatorProfile}
                      overallScore={
                        verdictV2
                          ? verdictV2.overall_score ?? undefined
                          : typeof analysisData?.overall_score === 'number'
                          ? analysisData.overall_score
                          : undefined
                      }
                      overallRating={verdictV2?.tier ?? analysisData?.overall_rating}
                      channelStats={
                        analysisData?.summary
                          ? {
                              scanned: analysisData.summary.total_videos_scanned ?? 0,
                              last12Months:
                                analysisData.summary.videos_last_year ??
                                analysisData.summary.total_videos_scanned ??
                                0,
                              analyzed: analysisData.summary.videos_analyzed ?? 0,
                              isInstagram: false,
                            }
                          : undefined
                      }
                      verdictV2={verdictV2}
                    />
                    <RiskSignalsGrid signals={creatorProfile?.risk_signals} />
                    <ChannelDashboard
                      data={analysisData}
                      hideIdentity={hideSidebar}
                      hideHero={hideSidebar}
                      verdictV2={verdictV2}
                    />
                    <div className="mt-6">
                      <NewsAnalysisSection news={newsAnalysis} />
                    </div>
                  </>
                )}
                {(analysisData?.error || taskResult?.status === 'FAILURE') && (() => {
                  const raw = String(analysisData?.error || taskResult?.error || '');
                  const isGeneric =
                    !raw.trim() ||
                    /something went wrong/i.test(raw) ||
                    /internal server/i.test(raw);
                  return (
                    <div
                      style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 12,
                        padding: '18px 22px',
                      }}
                    >
                      <h4
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          margin: '0 0 8px',
                          color: '#991b1b',
                          fontSize: 16,
                        }}
                      >
                        <AlertCircle size={16} />
                        Analysis unavailable
                      </h4>
                      <p
                        className="text-body-sm-regular text-red-700" style={{ margin: '0 0 14px', lineHeight: 1.5 }}
                      >
                        {isGeneric
                          ? "The AI service couldn't finish this channel's analysis. This usually clears up on a retry — please try again in a moment."
                          : raw}
                      </p>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => triggerAnalysis()}
                        disabled={polling || fakePolling}
                        style={{ fontSize: 13, fontWeight: 700 }}
                      >
                        Retry analysis
                      </Button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {activeMode === 'news' && newsReportOpen && (
          <div className={reportShellClass} style={{ background: '#F5F5F5' }}>
            {newsPolling ? (
              <div
                style={{ height: '100%', minHeight: 600, display: 'flex', flexDirection: 'column' }}
              >
                <ProcessingPanel agentType="brandsafety" elapsedSecs={newsElapsedSecs} />
              </div>
            ) : !newsTaskId && !newsResult ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  border: '1px solid #E5E5E5',
                  borderRadius: 16,
                }}
              >
                <div
                  className="flex items-center justify-center rounded-full border border-blue-200 bg-blue-50"
                  style={{ width: 80, height: 80, marginBottom: 20 }}
                >
                  <Search size={36} className="text-sky-700" />
                </div>
                <h3 className="m-0 text-heading-h5 text-foreground">
                  News Article Analysis
                </h3>
                <p
                  style={{
                    color: '#737373',
                    marginTop: 10,
                    textAlign: 'center',
                    maxWidth: 380,
                    fontSize: 15,
                    lineHeight: 1.7,
                  }}
                >
                  Search news coverage about a creator to identify controversies, brand signals, and
                  public sentiment trends.
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 20,
                    justifyContent: 'center',
                  }}
                >
                  {[
                    'Google News RSS',
                    'LLM categorization',
                    'Topic distribution',
                    'Controversy detection',
                    'Brand signals',
                    'Sentiment chart',
                  ].map((f) => (
                    <span
                      key={f}
                      style={{
                        fontSize: 15,
                        padding: '4px 12px',
                        borderRadius: 99,
                        background: '#eff6ff',
                        color: '#0369a1',
                        border: '1px solid #bfdbfe',
                        fontWeight: 600,
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ) : newsResult?.status === 'SUCCESS' && newsResult.result ? (
              <NewsResultsDashboard data={newsResult.result} />
            ) : newsResult?.status === 'FAILURE' ? (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 12,
                  padding: '18px 22px',
                  margin: 8,
                }}
              >
                <h4
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '0 0 8px',
                    color: '#991b1b',
                    fontSize: 16,
                  }}
                >
                  <AlertCircle size={16} />
                  Error
                </h4>
                <p className="m-0 text-red-600" style={{ fontFamily: 'monospace', fontSize: 16 }}>
                  {newsResult.error}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {hideSidebar && !channelReportOpen && !newsReportOpen && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              color: '#737373',
              fontSize: 14,
            }}
          >
            <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
            Preparing analysis...
          </div>
        )}

        {showSetupWizard && (
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
            <div className="flex items-start gap-6 pb-4">
              <div className="sticky top-6 flex w-[min(292px,100%)] shrink-0 flex-col gap-0 self-start rounded-xl border border-border bg-card p-5">
                <div className="mb-4 flex flex-col items-stretch gap-[10px]">
                  <p className="m-0 text-[10px] font-medium tracking-widest text-muted-foreground uppercase">
                    Progress
                  </p>
                  <div className="flex w-full flex-row flex-nowrap items-stretch gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="inline-flex flex-1 cursor-pointer items-center justify-center gap-[5px] rounded-full border border-border bg-muted px-[10px] py-2 text-caption-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                      onClick={handleNew}
                    >
                      <Plus size={14} /> New
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="inline-flex flex-1 cursor-pointer items-center justify-center gap-[5px] rounded-full border border-border bg-muted px-[10px] py-2 text-caption-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                      onClick={() => setIsHistoryOpen(true)}
                    >
                      <History size={14} /> History
                    </Button>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    paddingBottom: 10,
                    borderBottom: '1px solid var(--color-border-medium, #E5E5E5)',
                  }}
                >
                  <ShieldCheck size={18} color="#737373" />
                  <div>
                    <div className="text-body-sm-semibold text-foreground">
                      Brand Safety Agent
                    </div>
                    <div className="text-caption-regular text-muted-foreground">
                      Multi-signal channel &amp; news audit
                    </div>
                  </div>
                </div>
                {wizardSteps.map((step, idx) => {
                  const isActive = step.num === currentWizardStep;
                  const isCompleted = step.num < currentWizardStep;
                  return (
                    <React.Fragment key={step.num}>
                      <Button
                        type="button"
                        variant="ghost"
                        className="relative flex w-full cursor-pointer items-start gap-3 border-none bg-transparent p-0 text-left font-[inherit]"
                        onClick={() => activeMode === 'channel' && goChannelStep(step.num)}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: 'transparent',
                          cursor: activeMode === 'channel' ? 'pointer' : 'default',
                          padding: 0,
                          textAlign: 'left',
                          font: 'inherit',
                          height: 'auto',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <div className="flex shrink-0 flex-col items-center">
                          <div
                            className={`ncw-step-circle ${isActive ? 'ncw-step-circle--active' : isCompleted ? 'ncw-step-circle--completed' : ''}`}
                          >
                            {isCompleted ? <Check size={13} /> : step.num}
                          </div>
                          {idx < wizardSteps.length - 1 && (
                            <div className={`ncw-step-connector ${isCompleted ? 'ncw-step-connector--filled' : ''}`} />
                          )}
                        </div>
                        <div className="pt-1 pb-4">
                          <span
                            className={`block text-body-sm-semibold leading-snug ${isActive || isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}
                          >
                            {step.title}
                          </span>
                          <span className="mt-0.5 block text-caption-regular text-muted-foreground opacity-80">
                            {step.sub}
                          </span>
                        </div>
                      </Button>
                    </React.Fragment>
                  );
                })}
                <div className="mt-2 flex items-start gap-2 border-t border-border pt-3 text-caption-regular text-muted-foreground">
                  <Sparkles size={12} color="#000000" style={{ flexShrink: 0, marginTop: 1 }} />
                  AI scans content against your policies and surfaces risk signals.
                </div>
              </div>

              {activeMode === 'channel' ? (
                <form
                  className="flex min-h-[380px] flex-1 flex-col rounded-xl border border-border bg-card"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (channelWizardStep !== CHANNEL_WIZARD_STEPS.length) return;
                    void handleSubmit(e);
                  }}
                >
                  <div className="flex flex-1 flex-col gap-4 px-5 py-4">
                    <div
                      style={{
                        display: 'flex',
                        gap: 0,
                        borderBottom: '1px solid #f5f5f5',
                        marginBottom: 8,
                      }}
                    >
                      {(['channel', 'news'] as const).map((mode) => (
                        <Button
                          key={mode}
                          type="button"
                          variant="ghost"
                          onClick={() => setActiveMode(mode)}
                          style={{
                            flex: 1,
                            padding: '8px 0',
                            fontSize: 13,
                            fontWeight: activeMode === mode ? 700 : 500,
                            borderBottom:
                              activeMode === mode ? '2px solid #171717' : '2px solid transparent',
                            borderRadius: 0,
                            background: 'none',
                            color: activeMode === mode ? '#171717' : '#737373',
                            height: 'auto',
                          }}
                        >
                          {mode === 'channel' ? 'Channel Analysis' : 'News Analysis'}
                        </Button>
                      ))}
                    </div>
                    <div className="mb-0">
                      <h2 className="m-0 mb-0.5 text-heading-h6 text-foreground">
                        {stepMeta?.title}
                      </h2>
                      <p className="m-0 text-body-sm-regular text-muted-foreground">{stepMeta?.sub}</p>
                    </div>
                    {channelWizardStep === 1 && (
                      <div style={sectionCss}>
                        <label style={labelCss}>
                          <YoutubeIcon size={12} color="#dc2626" /> YouTube Channel URL
                        </label>
                        <Input
                          type="url"
                          required
                          value={formData.channel_url}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, channel_url: e.target.value }))
                          }
                          placeholder="https://www.youtube.com/@mkbhd"
                          className="w-full rounded-[7px] border-border bg-card px-[11px] py-2 text-body-sm-regular text-foreground outline-none"
                        />
                        <div className="text-caption-regular text-muted-foreground" style={{ marginTop: 6 }}>
                          All videos from last 12 months · 500 comments/video · transcript + LLM
                        </div>
                      </div>
                    )}
                    {channelWizardStep === 2 && (
                      <div style={sectionCss}>
                        <div
                          className="border-b border-border text-muted-foreground"
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            marginBottom: 14,
                            paddingBottom: 10,
                          }}
                        >
                          Brand Policies
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 8,
                            }}
                          >
                            <label style={labelCss}>
                              <Ban size={12} color="#dc2626" /> Banned Keywords
                            </label>
                            <span className="text-[12px] font-semibold text-muted-foreground">
                              Defaults +{bannedKeywords.length} custom
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 5,
                              marginBottom: 8,
                              maxHeight: 120,
                              overflowY: 'auto',
                              padding: '4px 0',
                            }}
                          >
                            {bannedKeywords.map((kw) => (
                              <span
                                key={kw}
                                className="inline-flex items-center rounded-full border border-red-200 bg-red-50 text-caption-medium text-red-600"
                                style={{ gap: 4, padding: '3px 8px' }}
                              >
                                {kw}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Remove keyword"
                                  onClick={() =>
                                    setBannedKeywords((p) => p.filter((k) => k !== kw))
                                  }
                                  className="text-red-600 opacity-70"
                                  style={{ width: 16, height: 16, padding: 0 }}
                                >
                                  x
                                </Button>
                              </span>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Input
                              type="text"
                              value={bannedNewInput}
                              onChange={(e) => setBannedNewInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = bannedNewInput.trim().toLowerCase();
                                  if (val && !bannedKeywords.includes(val))
                                    setBannedKeywords((p) => [...p, val]);
                                  setBannedNewInput('');
                                }
                              }}
                              placeholder="Add keyword + Enter"
                              className="min-w-0 flex-1 rounded-[7px] border-border bg-card px-[11px] py-2 text-body-sm-regular text-foreground outline-none"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                const val = bannedNewInput.trim().toLowerCase();
                                if (val && !bannedKeywords.includes(val))
                                  setBannedKeywords((p) => [...p, val]);
                                setBannedNewInput('');
                              }}
                              className="border-red-200 bg-red-50 text-caption-medium text-red-600"
                            >
                              Add
                            </Button>
                          </div>
                          <div className="mt-1.5 text-caption-regular text-muted-foreground">
                            Includes common banned words by default. Add custom ones above.
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 8,
                            }}
                          >
                            <label style={labelCss}>
                              <AlertCircle size={12} color="#737373" /> Sensitive Topics
                            </label>
                            <span className="text-[12px] font-semibold text-muted-foreground">
                              Defaults +{sensitiveTopics.length} custom
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 5,
                              marginBottom: 8,
                              maxHeight: 120,
                              overflowY: 'auto',
                              padding: '4px 0',
                            }}
                          >
                            {sensitiveTopics.map((t) => (
                              <span
                                key={t}
                                className="inline-flex items-center rounded-full border border-border bg-muted text-caption-medium text-muted-foreground"
                                style={{ gap: 4, padding: '3px 8px' }}
                              >
                                {t}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Remove topic"
                                  onClick={() =>
                                    setSensitiveTopics((p) => p.filter((k) => k !== t))
                                  }
                                  className="text-muted-foreground opacity-70"
                                  style={{ width: 16, height: 16, padding: 0 }}
                                >
                                  x
                                </Button>
                              </span>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Input
                              type="text"
                              value={sensitiveNewInput}
                              onChange={(e) => setSensitiveNewInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = sensitiveNewInput.trim().toLowerCase();
                                  if (val && !sensitiveTopics.includes(val))
                                    setSensitiveTopics((p) => [...p, val]);
                                  setSensitiveNewInput('');
                                }
                              }}
                              placeholder="Add topic + Enter"
                              style={{ flex: 1 }}
                              className="rounded-[7px] border-border bg-card px-[11px] py-2 text-body-sm-regular text-foreground outline-none"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                const val = sensitiveNewInput.trim().toLowerCase();
                                if (val && !sensitiveTopics.includes(val))
                                  setSensitiveTopics((p) => [...p, val]);
                                setSensitiveNewInput('');
                              }}
                              className="text-caption-medium text-muted-foreground"
                            >
                              Add
                            </Button>
                          </div>
                          <div className="mt-1.5 text-caption-regular text-muted-foreground">
                            Includes common sensitive topics by default. Add custom ones above.
                          </div>
                        </div>
                      </div>
                    )}
                    {channelWizardStep === 3 && (
                      <div style={sectionCss}>
                        <label style={labelCss}>
                          <Target size={12} color="#0369a1" /> Target Platforms
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                          {PLATFORMS.map((p) => {
                            const active = formData.target_platforms.includes(p);
                            return (
                              <Button
                                key={p}
                                type="button"
                                variant="ghost"
                                onClick={() => togglePlatform(p)}
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: `1px solid ${active ? '#1c1917' : '#E5E5E5'}`,
                                  background: active ? '#1c1917' : '#fafafa',
                                  color: active ? '#fff' : '#737373',
                                  transition: 'all 0.12s',
                                }}
                              >
                                {p}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-border px-5 pt-4 pb-5">
                    <div>
                      {channelWizardStep > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          className="cursor-pointer rounded-full border border-border bg-white px-4 py-2 text-body-sm-medium text-muted-foreground transition-colors hover:border-border disabled:opacity-50"
                          onClick={() => goChannelStep(channelWizardStep - 1)}
                          disabled={polling}
                        >
                          Back
                        </Button>
                      )}
                    </div>
                    <span className="text-caption-medium text-muted-foreground">
                      Step {currentWizardStep} of {wizardSteps.length}
                    </span>
                    <div className="flex items-center gap-3">
                      {channelWizardStep < CHANNEL_WIZARD_STEPS.length ? (
                        <Button
                          type="button"
                          variant="default"
                          className="cursor-pointer rounded-full bg-primary px-5 py-2 text-body-sm-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                          onClick={() => goChannelStep(channelWizardStep + 1)}
                          disabled={channelWizardStep === 1 && !formData.channel_url.trim()}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          variant="default"
                          className="flex cursor-pointer items-center gap-2 rounded-full bg-(--brand-500) px-5 py-2 text-body-sm-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
                          disabled={polling}
                        >
                          {polling ? (
                            <>
                              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />{' '}
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <ShieldCheck size={15} /> Run Brand Safety Analysis
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              ) : (
                <form
                  className="flex min-h-[380px] flex-1 flex-col rounded-xl border border-border bg-card"
                  onSubmit={handleNewsSubmit}
                >
                  <div className="flex flex-1 flex-col gap-4 px-5 py-4">
                    <div
                      style={{
                        display: 'flex',
                        gap: 0,
                        borderBottom: '1px solid #f5f5f5',
                        marginBottom: 8,
                      }}
                    >
                      {(['channel', 'news'] as const).map((mode) => (
                        <Button
                          key={mode}
                          type="button"
                          variant="ghost"
                          onClick={() => setActiveMode(mode)}
                          style={{
                            flex: 1,
                            padding: '8px 0',
                            fontSize: 13,
                            fontWeight: activeMode === mode ? 700 : 500,
                            borderBottom:
                              activeMode === mode ? '2px solid #171717' : '2px solid transparent',
                            borderRadius: 0,
                            background: 'none',
                            color: activeMode === mode ? '#171717' : '#737373',
                            height: 'auto',
                          }}
                        >
                          {mode === 'channel' ? 'Channel Analysis' : 'News Analysis'}
                        </Button>
                      ))}
                    </div>
                    <div className="mb-0">
                      <h2 className="m-0 mb-0.5 text-heading-h6 text-foreground">
                        {stepMeta?.title}
                      </h2>
                      <p className="m-0 text-body-sm-regular text-muted-foreground">{stepMeta?.sub}</p>
                    </div>
                    <div style={sectionCss}>
                      <label style={labelCss}>
                        <Search size={12} color="#737373" /> Creator / Channel Name
                      </label>
                      <Input
                        type="text"
                        required
                        value={newsForm.creator_name}
                        onChange={(e) =>
                          setNewsForm((p) => ({ ...p, creator_name: e.target.value }))
                        }
                        placeholder="e.g. MrBeast, MKBHD, PewDiePie"
                        className="w-full rounded-[7px] border-border bg-card px-[11px] py-2 text-body-sm-regular text-foreground outline-none"
                      />
                      <div className="mt-1.5 text-caption-regular text-muted-foreground">
                        Full name or handle — used to search news articles
                      </div>
                    </div>
                    <div style={sectionCss}>
                      <label style={labelCss}>
                        <YoutubeIcon size={12} color="#dc2626" /> YouTube Channel Name (optional)
                      </label>
                      <Input
                        type="text"
                        value={newsForm.channel_name || ''}
                        onChange={(e) =>
                          setNewsForm((p) => ({ ...p, channel_name: e.target.value }))
                        }
                        placeholder="e.g. MrBeast (if different from creator name)"
                        className="w-full rounded-[7px] border-border bg-card px-[11px] py-2 text-body-sm-regular text-foreground outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-border px-5 pt-4 pb-5">
                    <span className="text-caption-medium text-muted-foreground">Step 1 of 1</span>
                    <div className="flex items-center gap-3">
                      <Button
                        type="submit"
                        variant="default"
                        className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2 text-body-sm-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                        disabled={newsPolling}
                      >
                        {newsPolling ? (
                          <>
                            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />{' '}
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search size={15} /> Analyze News Coverage
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
